import { datadogRum } from '@datadog/browser-rum';
import { defineStore } from 'pinia';
import { ref } from 'vue';
import type { RouteLocationNormalized } from 'vue-router';
import productFruits from '@/product-fruits';
import { segment } from '@/segment';
import { useCompanyStore } from '@/stores/company';
import { useLocationsStore } from '@/stores/locations';
import { useResourcesStore } from '@/stores/resources';
import { useServicesStore } from '@/stores/services';
import { useTreatwellStore } from '@/stores/treatwell';
import { useUserStore } from '@/stores/user';
import { useVatRatesStore, vatRatesFetched } from '@/stores/vat-rates';
import unleash from '@/unleash';
import { useServiceGroupsStore } from './service-groups';
import { useServicesPriceScheduleStore } from './services-price-schedule';

/** Options-API stores get `$reset`; composition stores may expose a custom `reset()`. */
type PiniaStoreResetTarget = {
  reset?: () => void;
  $reset?: () => void;
};

export const useSessionStore = defineStore('session', () => {
  const initialRoute = ref<RouteLocationNormalized | null>(null);
  const requiredDataLoaded = ref(false);
  const allDataLoaded = ref(false);

  const fetchAllData = async () =>
    new Promise<void>((resolve) => {
      const { getUser } = useUserStore();
      getUser().then(() => {
        const { user } = useUserStore();
        const { company } = useCompanyStore();

        if (!user || !user.verified || company.blocked) {
          requiredDataLoaded.value = true;
          resolve();
          return;
        }

        initSegment();
        productFruits.init();

        const { getResources } = useResourcesStore();
        const { getLocations } = useLocationsStore();
        const { getTreatwellStatus } = useTreatwellStore();
        const { getServices, getServiceCategories, getServicePackages } =
          useServicesStore();
        const { getServiceGroups } = useServiceGroupsStore();
        const { getScheduledPriceUpdate } = useServicesPriceScheduleStore();

        // The treatwell status decides on a KYC fetch based on a feature flag,
        // so it gets the Unleash promise instead of racing it.
        const unleashReady = startUnleash();
        Promise.all([
          getResources(),
          getLocations(),
          getTreatwellStatus(unleashReady),
          unleashReady,
        ]).then(() => {
          resolve();
          requiredDataLoaded.value = true;

          // These requests can be done after resolving the promise
          // They will run in the background when the app is already rendered
          Promise.all([
            getServices(),
            getServiceCategories(),
            getServiceGroups(),
            getScheduledPriceUpdate(),
            ...(unleash.isEnabled('ServicePackages')
              ? [getServicePackages()]
              : []),
          ]).then(() => {
            allDataLoaded.value = true;
          });

          const { generateFingerprint, fetchOrCreateSystem, hasFeatureFlag } =
            useUserStore();
          generateFingerprint().then(() => {
            if (
              hasFeatureFlag('fiskaly-sign-enabled') &&
              hasFeatureFlag('compliant-register')
            ) {
              fetchOrCreateSystem();
            }
          });
        });
      });
    });

  const initSegment = () => {
    const { user } = useUserStore();
    const { company } = useCompanyStore();
    segment.identify(String(user?.id), {
      email: user?.email,
      name: user?.fullName,
      user_roles: {
        admin: user?.admin,
        basic: user?.basic,
        manager: user?.manager,
        owner: user?.owner,
        reports: user?.reports,
      },
    });
    segment.group(String(company?.id), {
      company_name: company?.name,
      company_country_code: company?.country?.toUpperCase(),
      company_language_code: company?.language,
    });
  };

  const startUnleash = () =>
    new Promise<void>((resolve) => {
      const { user } = useUserStore();
      const { company } = useCompanyStore();

      // Set Unleash context data
      unleash.updateContext({
        userId: String(user?.id),
        properties: company.unleashContextProperties,
      });

      const timeout = setTimeout(() => {
        // Make sure that the promise resolves if Unleash doesn't load for whatever reason
        // So that it doesn't block loading the entire application
        resolve();
      }, 2000);

      // The ready event triggers when the first Unleash request finishes
      unleash.on('ready', () => {
        clearTimeout(timeout);
        resolve();
      });

      // When Unleash errors, just continue
      unleash.on('error', () => {
        clearTimeout(timeout);
        resolve();
      });

      // Start Unleash, which will trigger an initial request
      // Make sure this gets done after registering the event listeners
      unleash.start();
    });

  const resetData = () => {
    const stores = [
      useUserStore(),
      useCompanyStore(),
      useLocationsStore(),
      useResourcesStore(),
      useServicesStore(),
    ];

    stores.forEach((store) => {
      const s = store as PiniaStoreResetTarget;
      if (typeof s.reset === 'function') {
        s.reset();
      } else if (typeof s.$reset === 'function') {
        s.$reset();
      }
    });

    if (vatRatesFetched.value) {
      const { reset } = useVatRatesStore();
      reset();
    }

    datadogRum.removeUser();
    segment.reset();
    productFruits.destroy();

    allDataLoaded.value = false;
  };

  return {
    initialRoute,
    requiredDataLoaded,
    allDataLoaded,
    fetchAllData,
    startUnleash,
    resetData,
  };
});
